import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Select,
  Paper,
  Table,
  Divider,
  Skeleton,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import { IconPackage, IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { ServicePackage, PackageReasonCategory } from "../../types/packages";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import ReasonFields from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface AddPackageToClientModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  clientId: string;
  /** Refetch the client's owned packages after a successful grant. */
  onSuccess: () => void | Promise<void>;
}

/**
 * Grant a catalog service package to a client (a manual, 100%-comped copy of a
 * package the salon offers). The salon's package catalog is fetched via
 * `getServicePackages`; every grant records a `grant_package` adjustment with a
 * required reason + note (mirrors `RefundPaymentModal`'s submit-gating UX).
 */
export default function AddPackageToClientModal({
  opened,
  onClose,
  salonId,
  clientId,
  onSuccess,
}: AddPackageToClientModalProps) {
  const { isMobile } = useResponsive();
  const { getServicePackages, addClientServicePackage } = useApp();

  const [catalog, setCatalog] = useState<ServicePackage[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [packageId, setPackageId] = useState<string | null>(null);
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    setIsLoadingCatalog(true);
    setCatalogError(null);
    try {
      const list = await getServicePackages(salonId);
      // Only active (non-archived) catalog packages can be granted.
      setCatalog(list.filter((pkg) => pkg.status !== "archived"));
    } catch (err) {
      console.error("Error fetching service package catalog:", err);
      setCatalogError("Failed to load the package catalog.");
    } finally {
      setIsLoadingCatalog(false);
    }
  }, [getServicePackages, salonId]);

  useEffect(() => {
    if (opened) {
      setPackageId(null);
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
      loadCatalog();
    }
  }, [opened, loadCatalog]);

  const selectedPackage = catalog.find((pkg) => pkg.id === packageId) ?? null;
  const canSubmit = packageId !== null && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || packageId === null) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await addClientServicePackage(salonId, clientId, {
        service_package_id: packageId,
        reason_category: category,
        note: note.trim(),
      });
      notifySuccess(
        `${selectedPackage?.name ?? "Package"} added to the client.`,
        {
          title: "Package granted",
        },
      );
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in add-package success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error granting package:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to grant the package. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Add Package</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {catalogError ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            <Group justify="space-between" align="center">
              <Text size="sm">{catalogError}</Text>
              <Button
                size="compact-xs"
                variant="light"
                color="red"
                onClick={loadCatalog}
              >
                Retry
              </Button>
            </Group>
          </Alert>
        ) : isLoadingCatalog ? (
          <Skeleton height={60} radius="sm" />
        ) : catalog.length === 0 ? (
          <Text size="sm" c="dimmed">
            No packages are available to grant. Create one in Settings first.
          </Text>
        ) : (
          <Select
            label="Package"
            placeholder="Select a package to grant"
            data={catalog.map((pkg) => ({
              value: pkg.id,
              label:
                pkg.kind === "special" ? `${pkg.name} (Special)` : pkg.name,
            }))}
            value={packageId}
            onChange={setPackageId}
            required
            disabled={isSubmitting}
          />
        )}

        {selectedPackage && (
          <Paper p="sm" withBorder radius="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={600}>
                  {selectedPackage.name}
                </Text>
                <Text size="sm" fw={600} c="var(--brand-teal)">
                  {formatCurrency(selectedPackage.price)}
                </Text>
              </Group>
              {selectedPackage.items.length > 0 && (
                <Table verticalSpacing={4} fz="sm" withRowBorders={false}>
                  <Table.Tbody>
                    {selectedPackage.items.map((item) => (
                      <Table.Tr key={item.salon_service_id}>
                        <Table.Td>{item.service_name}</Table.Td>
                        <Table.Td ta="right">×{item.quantity}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        )}

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
          categoryRequired={false}
          noteRequired={false}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconPackage size={18} />}
          >
            Add Package
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
